import { ListFilter, Rows3, Rows4 } from '@orgus/ui/icons';
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { Button, DensityToggle, FilterChips, SavedViewsMenu, Tooltip, isTypingTarget, toast, toggleDensity, undoable, useDensity } from '@orgus/ui';
import type { FilterChip } from '@orgus/ui';
import { openPalette, useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { clauseChip } from '../ask/chips.ts';
import type { AskEntity } from '../ask/parse.ts';
import { deleteMyView, matchView, normalizeSearch, restoreMyView, saveMyView, useViews, viewHref, viewsFor } from './store.ts';
import { deleteTeamView, loadTeamViews, shareView } from './team.ts';
import type { Where } from './useWhere.ts';

export interface ViewBarProps {
  entity: AskEntity;
  where: Where;
  /** The page's own filters from the address (status, machine, search), as chips. */
  extraChips?: FilterChip[];
  /** Clears the page's own filters (the where clauses clear themselves). */
  onClearExtra?: () => void;
  trailing?: ReactNode;
  className?: string;
  /** The density toggle: off on lists whose rows have one fixed height (Sessions, h-row). */
  density?: boolean;
}

const ENTITY_WORD: Record<AskEntity, string> = {
  sessions: 'sessions',
  machines: 'machines',
  projects: 'worktrees',
  audit: 'audit',
  spend: 'spend',
  pipelines: 'pipelines',
  checkouts: 'worktrees',
};

/**
 * The filter bar of a list page (DesignSystem 6.1): saved views, the active filters as chips
 * (each one mirrored in the address), a words filter that opens Ask, and the density toggle.
 */
export function ViewBar({ entity, where, extraChips = [], onClearExtra, trailing, className, density: showDensity = true }: ViewBarProps) {
  const { pathname, search } = useLocation();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const mine = useViews((s) => s.mine);
  const team = useViews((s) => s.team);
  const teamOk = useViews((s) => s.teamAvailable);
  const views = useMemo(() => viewsFor(pathname, { mine, team }), [pathname, mine, team]);
  const normalized = normalizeSearch(pathname, search);
  const current = matchView(pathname, search, views);
  const linked = params.get('view');

  useEffect(() => {
    void loadTeamViews();
  }, []);

  // f: describe a filter in words.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'f' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]')) return;
      e.preventDefault();
      openPalette(`? ${ENTITY_WORD[entity]} ${where.text}${where.text ? ' ' : ''}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [entity, where.text]);

  const density = useDensity();
  const cmds = useMemo<Command[]>(
    () => [
      { id: 'view.filter', title: 'Filter this list in words', group: 'General', icon: ListFilter, shortcut: ['f'], perform: () => openPalette(`? ${ENTITY_WORD[entity]} ${where.text}${where.text ? ' ' : ''}`) },
      {
        id: 'view.density',
        title: density === 'compact' ? 'Comfortable rows' : 'Compact rows',
        subtitle: 'Row density for every list',
        group: 'General',
        icon: density === 'compact' ? Rows3 : Rows4,
        keywords: ['density', 'dense', 'spacing'],
        perform: toggleDensity,
      },
    ],
    [entity, where.text, density],
  );
  useRegisterCommands(cmds, [cmds]);

  const chips: FilterChip[] = [
    ...extraChips,
    ...where.clauses.map((c, i): FilterChip => {
      const { field, value } = clauseChip(c);
      return { id: `where:${i}:${field}`, field, value, onRemove: () => where.remove(i) };
    }),
  ];

  const clearAll = () => {
    onClearExtra?.();
    where.clear();
  };

  return (
    <FilterChips
      className={className}
      chips={chips}
      onClearAll={clearAll}
      leading={
        <>
          <SavedViewsMenu
            views={views.map((v) => ({ id: v.id, name: v.name, shared: v.shared, summary: summaryOf(v.search) }))}
            currentId={current?.id ?? null}
            linkedName={!current && linked ? linked : null}
            canSave={!!normalized}
            canShare={teamOk}
            onApply={(id) => {
              const v = views.find((x) => x.id === id);
              if (v) navigate(viewHref(v));
            }}
            onSave={async (name, { shared }) => {
              if (shared) {
                await shareView({ name, path: pathname, search: normalized }).catch((e: unknown) => {
                  toast.error(e);
                  throw e;
                });
                toast.success(`Shared "${name}"`, 'Everyone on this hub sees it under Views.');
              } else {
                const v = saveMyView({ name, path: pathname, search: normalized });
                undoable({ title: `Saved "${name}"`, description: 'Open it from Views or the command palette.', undo: () => void deleteMyView(v.id) });
              }
              setParams(
                (p) => {
                  const n = new URLSearchParams(p);
                  n.set('view', name);
                  return n;
                },
                { replace: true, preventScrollReset: true },
              );
            }}
            onDelete={(id) => {
              const v = views.find((x) => x.id === id);
              if (!v) return;
              if (v.shared) {
                void deleteTeamView(id)
                  .then(() => toast.success(`Deleted "${v.name}"`, 'It is gone for everyone on this hub.'))
                  .catch((e: unknown) => toast.error(e));
                return;
              }
              const gone = deleteMyView(id);
              if (gone) undoable({ title: `Deleted "${gone.name}"`, undo: () => restoreMyView(gone) });
            }}
            onCopyLink={() => {
              const name = current?.name ?? linked;
              const p = new URLSearchParams(normalized);
              if (name) p.set('view', name);
              const qs = p.toString();
              const url = `${window.location.origin}${pathname}${qs ? `?${qs}` : ''}`;
              void navigator.clipboard
                ?.writeText(url)
                .then(() => toast.success('Link copied', 'Anyone signed in to this hub sees the same list.'))
                .catch(() => toast('Copy this link', { description: url, duration: 10_000 }));
            }}
          />
          <Tooltip content="Filter in words, for example: failed today on build-01" shortcut={['f']}>
            <Button size="sm" variant="ghost" icon={<ListFilter />} onClick={() => openPalette(`? ${ENTITY_WORD[entity]} ${where.text}${where.text ? ' ' : ''}`)}>
              Filter
            </Button>
          </Tooltip>
        </>
      }
      trailing={
        <>
          {trailing}
          {showDensity && <DensityToggle />}
        </>
      }
    />
  );
}

/** One line for the views menu: "where failed today, group machine". */
function summaryOf(search: string): string {
  const p = new URLSearchParams(search);
  const parts: string[] = [];
  for (const [k, v] of p) parts.push(k === 'where' ? v : `${k} ${v}`);
  return parts.join(', ');
}
